import { ApiService } from '../services/ApiService';
import type {
	CreateSavedSearchRequest,
	ListSavedSearchesParams,
	SavedSearchModel,
	ShareableRolesResponse,
	UpdateSavedSearchRequest,
} from './types';

const BASE_PATH = '/saved-searches';

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class SavedSearchQueryService {
	/**
	 * List all saved searches for a list key.
	 * Results include defaults, user's own, and shared searches.
	 * Sorted by totalUseCount (popularity) descending.
	 */
	public static async list(params: ListSavedSearchesParams): Promise<{
		success: boolean;
		data: Array<SavedSearchModel>;
		error?: string;
	}> {
		try {
			const queryParams: Record<string, string> = {
				listKey: params.listKey,
			};

			if (params.search != null && params.search.length > 0) {
				queryParams.search = params.search;
			}

			if (params.types != null && params.types.length > 0) {
				queryParams.types = params.types.join(',');
			}

			if (params.allNonPrivate === true) {
				queryParams.allNonPrivate = 'true';
			}

			const response = await ApiService.apiGet<{
				success: boolean;
				data: Array<SavedSearchModel>;
			}>(BASE_PATH, queryParams);

			return {
				success: true,
				data: response?.data ?? [],
			};
		} catch (error) {
			console.error('Error fetching saved searches:', error);
			return {
				success: false,
				data: [],
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch saved searches',
			};
		}
	}

	/**
	 * Get a single saved search by ID
	 */
	public static async getById(id: number): Promise<{
		data: SavedSearchModel | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: SavedSearchModel;
			}>(`${BASE_PATH}/${id}`);

			return {
				data: response?.data ?? null,
				success: true,
			};
		} catch {
			return {
				data: null,
				success: false,
				error: 'Failed to fetch saved search',
			};
		}
	}

	/**
	 * Create a new saved search.
	 * The creating user becomes the manager.
	 */
	public static async create(data: CreateSavedSearchRequest): Promise<{
		data: SavedSearchModel | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiPost<{
				success: boolean;
				data: SavedSearchModel;
			}>(BASE_PATH, data);

			return {
				data: response?.data ?? null,
				success: true,
			};
		} catch (error) {
			console.error('Error creating saved search:', error);

			return {
				data: null,
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to create saved search',
			};
		}
	}

	/**
	 * Update an existing saved search.
	 * Requires either: manager status, or allowEdit = true on the list.
	 */
	public static async update(
		id: number,
		data: UpdateSavedSearchRequest,
	): Promise<{
		data: SavedSearchModel | null;
		success: boolean;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiPut<{
				success: boolean;
				data: SavedSearchModel;
			}>(`${BASE_PATH}/${id}`, data);

			return {
				data: response?.data ?? null,
				success: true,
			};
		} catch (error) {
			console.error('Error updating saved search:', error);

			return {
				data: null,
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to update saved search',
			};
		}
	}

	/**
	 * Delete a saved search (soft delete).
	 * Only the manager can delete.
	 */
	public static async delete(
		id: number,
	): Promise<{ success: boolean; error?: string }> {
		try {
			await ApiService.apiDelete(`${BASE_PATH}/${id}`);

			return { success: true };
		} catch (error) {
			console.error('Error deleting saved search:', error);

			return {
				success: false,
				error:
					error instanceof Error
						? error.message
						: 'Failed to delete saved search',
			};
		}
	}

	/**
	 * Get roles the current user can share saved searches with.
	 * If user has role management permission, returns all roles.
	 * Otherwise, returns only roles they are a member of.
	 */
	public static async getShareableRoles(): Promise<{
		success: boolean;
		data: ShareableRolesResponse;
		error?: string;
	}> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data: ShareableRolesResponse;
			}>(`${BASE_PATH}/shareable-roles`);

			return {
				success: true,
				data: response?.data ?? { roles: [], canShareWithAny: false },
			};
		} catch (error) {
			console.error('Error fetching shareable roles:', error);
			return {
				success: false,
				data: { roles: [], canShareWithAny: false },
				error:
					error instanceof Error
						? error.message
						: 'Failed to fetch shareable roles',
			};
		}
	}
}
